import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import ModalContext from 'contexts/ModalContext'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import useRebrandVersion from 'hooks/useRebrandVersion'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

import MaldivesCharterRewardSeatsInfoModal from './MaldivesCharterRewardSeatsInfoModal'

const BannerRoot = styled.div`
  border-top: 1px solid ${(props) => props.theme.palette.highlight.primary.darkForeground};
`

const LearnMoreButton = styled(Clickable)`
  text-decoration: underline;
  text-underline-offset: ${rem(2)};
  white-space: nowrap;
  color: ${(props) => props.theme.palette.highlight.primary.darkForeground};
`

const RebrandRewardSeatsDescription = styled(BodyText)`
  margin-inline-start: ${rem(4)};
`

interface Props {
  onViewCharterDates?: () => void
}

function MaldivesCharterBundleEducationBanner({ onViewCharterDates }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const isRewardSeatsEnabled = useRewardSeatsEnabled()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const openInfoModal = useCallback(async() => {
    const viewDates = await showModal<boolean>(
      <MaldivesCharterRewardSeatsInfoModal />,
    )
    if (viewDates) {
      onViewCharterDates?.()
    }
  }, [showModal, onViewCharterDates])

  const learnMoreLabel = intl.formatMessage({
    id: '6oeFO6pEajqbz1fHSq3A',
    defaultMessage: 'Learn more',
    description: 'Text link on the right of the Maldives charter reward-seats banner that opens an in-app modal explaining Classic Rewards seats and how to find them.',
  })

  const rebrandHeading = intl.formatMessage({
    id: 'pkZiH5we_ABs-pFRnDHr',
    defaultMessage: 'Reward seats now available',
    description: 'Short rebrand heading for the Maldives charter reward-seats message banner.',
  })

  const rewardSeatsDescription = intl.formatMessage(
    {
      id: 'SW1gzan-Z97YXa9XH9yZ',
      defaultMessage: 'The ultimate way to spend your {loyaltyProgramName} Points. Limited availability.',
      description: 'Secondary sentence on the Maldives charter reward-seats banner after the headline, explaining loyalty points use and limited reward inventory.',
    },
    { loyaltyProgramName },
  )

  const secondaryActionButtonProps = useMemo(() => ({
    children: learnMoreLabel,
    onClick: openInfoModal,
  }), [learnMoreLabel, openInfoModal])

  if (!isRewardSeatsEnabled) {
    return null
  }

  if (isRebrand) {
    return <MessageBanner
      kind="informational"
      density="compact"
      icon={<ClassicRewardsSealIcon size={28} colour="highlight-primary-dark" />}
      secondaryActionButtonProps={secondaryActionButtonProps}
    >
      <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">
        {rebrandHeading}
      </BodyText>
      <RebrandRewardSeatsDescription variant="medium" colour="light-foreground-alt" as="span">
        {rewardSeatsDescription}
      </RebrandRewardSeatsDescription>
    </MessageBanner>
  }

  return (
    <BannerRoot>
      <Pane background="highlight-primary" padding="12 16">
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={12} wrap="wrap">
          <Group direction="vertical" gap={4}>
            <Group direction="horizontal" verticalAlign="center" gap={8}>
              <ClassicRewardsSealIcon size={28} colour="highlight-primary-dark" />
              <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">
                {intl.formatMessage(
                  {
                    id: '7fvUULCNrKdoFSg7KDo6',
                    defaultMessage: '{loyaltyProgramName} Rewards seats now available',
                    description: 'Bold teal headline on the horizontal Maldives charter banner introducing Société Rewards seats beside the seal icon.',
                  },
                  { loyaltyProgramName },
                )}
              </BodyText>
            </Group>
            <BodyText variant="medium" colour="neutral-one" as="span">
              {rewardSeatsDescription}
            </BodyText>
          </Group>
          <LearnMoreButton onClick={openInfoModal}>
            <BodyText variant="medium" colour="highlight-primary-dark" as="span">
              {learnMoreLabel}
            </BodyText>
          </LearnMoreButton>
        </Group>
      </Pane>
    </BannerRoot>
  )
}

export default MaldivesCharterBundleEducationBanner
